import { Dictionary, compact, last, flatMap } from 'lodash';
import { TagDefinition, Site, TimeSeries, ComponentId } from '../../../models';
import { ComponentFlow } from './types';
import { includesAll } from '../../../helpers/common';
import { tagAttributes, AttrType } from '../../../models/site';
const ACTIVE_LIMIT = 0.1;

const getTags = (attributes: string[][], tagLookup: Dictionary<TagDefinition>) => {
  const tags = Object.values(tagLookup);

  const allMatchingTags = flatMap(attributes, (attributeList) => {
    return tags.filter((tag) => includesAll(tag.attributes, attributeList));
  });

  const uniqueTagIds = new Set<string>();
  const uniqueTags = allMatchingTags.filter((tag) => {
    if (uniqueTagIds.has(tag.id)) return false;
    uniqueTagIds.add(tag.id);
    return true;
  });

  return uniqueTags;
};

export const getTagsByAttributes = (
  tagLookup: Dictionary<TagDefinition>,
  tagType: AttrType
): TagDefinition[] => {
  const attributeMap = Object.values(tagAttributes);
  let arrAttributes: string[][][] = [];
  if (tagType === AttrType.ExtraAttributes) {
    arrAttributes = attributeMap
      .map((t) => ('extraAttributes' in t ? t.extraAttributes : []))
      .filter((arrays) => arrays.length > 0);
  } else {
    arrAttributes = attributeMap
      .map((t) => (t[tagType as keyof typeof t] as string[][]) || [])
      .filter((arrays) => arrays.length > 0);
  }
  // flatten the array
  const flattenedAttributes = flatMap(arrAttributes);
  return getTags(flattenedAttributes, tagLookup);
};

export const getMetricTags = (
  site: Site,
  tagLookup: Dictionary<TagDefinition>
): TagDefinition[] => {
  const elements = site.webPortalLayoutJson.lobby?.kPIs?.elements || [];
  const attributes = elements.map((e) => e.attributes);
  return getTags(attributes, tagLookup);
};

/**
 * buildFlows
 *
 * Returns a list of flows configured for this site, if there is a tag
 * in the dictionary which matches the attributes defined for a particular
 * flow then the site is configured to have that flow.
 *
 */
export const buildFlows = (
  snapshots: Dictionary<TimeSeries>,
  linkTags: TagDefinition[]
): ComponentFlow[] => {
  // Get all possible components from tagAttributes and filter only those that have the specified tagType
  const possibleComponents = Object.keys(tagAttributes);

  // Map component keys to ComponentId enum values
  const componentIds = compact(
    possibleComponents.map((key) =>
      Object.values(ComponentId).find((id) => id.toLowerCase() === key.toLowerCase())
    )
  );

  // Build flows for each component
  const flows = componentIds.map((componentId) => {
    const componentKey = componentId.toLowerCase() as keyof typeof tagAttributes;
    const component = tagAttributes[componentKey];
    const attributesList = component.current;
    let latestValue = undefined;

    // Check each set of attributes until we find a match
    for (const attributes of attributesList) {
      const matchedTimeSeries = Object.values(snapshots).find((snapshot: TimeSeries) => {
        if (snapshot.points.length === 0) {
          return false;
        }
        return includesAll(snapshot.tag.attributes, attributes);
      });

      if (matchedTimeSeries) {
        latestValue = last(matchedTimeSeries.points)?.value;
        break; // Found a match, exit the loop
      }
    }

    const out = {
      id: componentId,
      name: componentId as string,
      flow: latestValue,
      installed: latestValue !== undefined,
      active: latestValue && Math.abs(latestValue) >= ACTIVE_LIMIT,
      unit: undefined, // Will be updated if we find a matched time series
    } as ComponentFlow;

    // Find a matching time series to get the unit and tag ID
    for (const attributes of attributesList) {
      const matchedTimeSeries = Object.values(snapshots).find((snapshot: TimeSeries) => {
        return includesAll(snapshot.tag.attributes, attributes);
      });

      if (matchedTimeSeries) {
        out.unit = matchedTimeSeries.tag.unit;
        break;
      }
    }

    // Handle additional metrics for components that have extraAttributes
    if ('extraAttributes' in component) {
      const extraMetrics: Record<string, number | string | undefined> = {};

      component.extraAttributes.forEach((attributes) => {
        const matchedExtraTimeSeries = Object.values(snapshots).find((snapshot: TimeSeries) => {
          return includesAll(snapshot.tag.attributes, attributes);
        });

        if (matchedExtraTimeSeries) {
          const value = last(matchedExtraTimeSeries.points)?.value;
          if (value !== undefined) {
            // Use the last attribute as the key (e.g., 'state_of_charge', 'storage_hz')
            const key = attributes[attributes.length - 1].split(' ').join('_');
            extraMetrics[key] = value === null ? undefined : value;
            extraMetrics[key + '_unit'] = matchedExtraTimeSeries.tag.unit;

            // TODO: remove this once we have a proper way to check if the component is installed
            // there are some sites that are not getting flow data but we are getting state of charge data. This is a backend issue.
            // if we are not getting flow data but we are getting state of charge data, we should consider the component installed
            if (key === 'state_of_charge' && !out.installed) {
              out.installed = true;
            }
          }
        }
      });

      if (Object.keys(extraMetrics).length > 0) {
        out.additionalMetrics = extraMetrics;
      }
    }

    // add ids for the link to historical data
    out.linkTagsId = linkTags.reduce((acc, tag) => {
      // Check each set of historical attributes
      for (const historicalAttributes of component.historical) {
        if (includesAll(tag.attributes, historicalAttributes) && !acc.includes(tag.id)) {
          acc.push(tag.id);
        }
      }
      return acc;
    }, [] as string[]);

    return out;
  });

  return flows;
};

/**
 * How close should flow be to 0 before we consider it 0?
 */
export const FLOW_TOL = 0.5;
